/**
 * Billing: Stripe Checkout (Base/Pro) and Customer Portal.
 * Uses a single Edge Function invoke wrapper with robust error logging.
 */

import { supabase } from '@/integrations/supabase/client';

export type BillingPlan = 'base' | 'pro';

const EDGE_ORIGIN = (() => {
  try {
    const url = import.meta.env.VITE_SUPABASE_URL;
    if (url && typeof url === 'string') {
      const u = new URL(url);
      return `${u.origin}/functions/v1`;
    }
  } catch {
    // ignore
  }
  return null;
})();

/**
 * Invoke an Edge Function with robust error logging.
 * Logs fn name, safe body (no secrets), and full error/details for debugging.
 */
export async function invokeEdge<T = unknown>(
  fn: string,
  body: Record<string, unknown>
): Promise<{ data: T; error: null } | { data: null; error: Error }> {
  const safeBody = { ...body };
  if (import.meta.env.DEV) {
    console.debug('[billing] invoke', fn, {
      bodyKeys: Object.keys(safeBody),
      workspace_id: safeBody.workspace_id,
      plan: safeBody.plan,
      origin: EDGE_ORIGIN ?? 'unknown',
    });
  }

  const { data, error } = await supabase.functions.invoke(fn, { body: safeBody });

  if (error) {
    const status = (error as { context?: { status?: number } })?.context?.status ?? (error as { status?: number })?.status;
    if (import.meta.env.DEV) {
      console.debug('[billing] error', fn, { status, message: error.message });
    }
    const message =
      error.message ||
      (status === 409 ? 'You need to complete a subscription first.' : 'Failed to send a request to the Edge Function. Check console and network (CORS, URL, auth).');
    console.error('[invokeEdge] Error response', {
      fn,
      status,
      message: error.message,
      context: (error as any)?.context,
    });
    const err = new Error(`${fn} — ${status ?? 'non-2xx'} — ${message}`) as Error & { code?: string };
    if (status === 409) err.code = 'NO_CUSTOMER';
    return { data: null, error: err };
  }

  const payload = data as { error?: string; missing_env?: string[] };
  const errFromPayload = payload?.error;
  if (errFromPayload) {
    const missing = payload?.missing_env;
    const msg = missing?.length
      ? `Server configuration error: missing ${missing.join(', ')}`
      : errFromPayload;
    console.error('[invokeEdge] Function returned error', { fn, error: errFromPayload, missing_env: missing });
    const err = new Error(msg) as Error & { code?: string };
    if (errFromPayload === 'no_customer') err.code = 'NO_CUSTOMER';
    return { data: null, error: err };
  }

  return { data: data as T, error: null };
}

/**
 * Start Stripe Checkout for the given plan and workspace (14-day trial).
 * Redirects to Stripe Checkout on success.
 */
export async function startCheckout(plan: BillingPlan, workspaceId: string): Promise<void> {
  const result = await invokeEdge<{ url?: string }>('create-checkout-session', {
    plan,
    workspace_id: workspaceId,
  });

  if (result.error) throw result.error;

  const url = result.data?.url;
  if (!url || typeof url !== 'string') {
    console.error('[startCheckout] No url in response', result.data);
    throw new Error('No checkout URL returned. Please try again or contact support.');
  }

  window.location.href = url;
}

/**
 * Open Stripe Customer Portal (manage subscription, payment methods, invoices).
 * Redirects to Stripe; return URL is /app/settings/billing.
 * Throws with code 'NO_CUSTOMER' when workspace/user has no Stripe customer (upgrade first).
 */
export async function openBillingPortal(workspaceId?: string): Promise<void> {
  const body: Record<string, unknown> = {};
  if (workspaceId) body.workspace_id = workspaceId;

  const result = await invokeEdge<{ url?: string }>('create-portal-session', body);

  if (result.error) {
    const err = result.error as Error & { code?: string };
    if (err.code === 'NO_CUSTOMER') {
      const e = new Error('Upgrade to Pro first to manage billing.') as Error & { code?: string };
      e.code = 'NO_CUSTOMER';
      throw e;
    }
    throw result.error;
  }

  const url = result.data?.url;
  if (!url || typeof url !== 'string') {
    console.error('[openBillingPortal] No url in response', result.data);
    throw new Error('No portal URL returned. You may need to complete a checkout first.');
  }

  window.location.href = url;
}
